import * as React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Title from "../Common/Title";
import { getOutOfStockProducts } from "../../Utils/vendor-customer-service";
import {
  extractIdFromGid,
  SHOPIFY_PRODUCT_STORE_URL,
} from "../../Utils/constant";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const openShopifyProductInventory = (productGid) => {
  const productId = extractIdFromGid(productGid);
  if (!SHOPIFY_PRODUCT_STORE_URL || !productId) {
    return;
  }
  const url = `${SHOPIFY_PRODUCT_STORE_URL}/${productId}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

export default function OutOfStockProducts() {
  const [items, setItems] = React.useState([]);
  const [cursor, setCursor] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [titleInput, setTitleInput] = React.useState("");
  const [titleQuery, setTitleQuery] = React.useState("");
  const [lightbox, setLightbox] = React.useState({
    open: false,
    src: "",
    title: "",
  });

  const openLightbox = (src, title) => {
    if (!src) {
      return;
    }
    setLightbox({ open: true, src, title: title || "Product" });
  };

  const closeLightbox = () => {
    setLightbox({ open: false, src: "", title: "" });
  };

  React.useEffect(() => {
    const handle = setTimeout(() => {
      setTitleQuery(titleInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [titleInput]);

  const fetchPage = React.useCallback(
    (nextCursor, replace = false, title = titleQuery) => {
      return getOutOfStockProducts(PAGE_SIZE, nextCursor, title || null)
        .then((response) => {
          if (response?.status !== 200) {
            setError("Unable to load out-of-stock products.");
            setHasMore(false);
            return;
          }

          const nodes = response?.data?.nodes || [];
          const pageInfo = response?.data?.pageInfo || {};

          setItems((prev) => (replace ? nodes : [...prev, ...nodes]));
          setCursor(pageInfo.endCursor || null);
          setHasMore(Boolean(pageInfo.hasNextPage));
          setError("");
        })
        .catch(() => {
          setError("Unable to load out-of-stock products.");
          setHasMore(false);
        })
        .finally(() => {
          setInitialLoading(false);
        });
    },
    [titleQuery]
  );

  React.useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(true);
    setInitialLoading(true);
    setError("");
    fetchPage(null, true, titleQuery);
  }, [fetchPage, titleQuery]);

  const loadMore = () => {
    if (!hasMore) {
      return;
    }
    fetchPage(cursor, false, titleQuery);
  };

  const clearSearch = () => {
    setTitleInput("");
  };

  return (
    <React.Fragment>
      <Title>Out of Stock Products</Title>
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="body1">
          Active products with no available tracked inventory
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={titleInput}
          onChange={(e) => setTitleInput(e.target.value)}
          placeholder="Search by product title"
          sx={{ mt: 1.5, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: titleInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={clearSearch}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Showing {items.length} loaded
          {titleQuery ? ` for “${titleQuery}”` : ""}
          {hasMore ? " (more available)" : ""}
        </Typography>
      </Box>

      {error ? <Typography color="error">{error}</Typography> : null}

      {initialLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <InfiniteScroll
          dataLength={items.length}
          next={loadMore}
          hasMore={hasMore}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          }
          endMessage={
            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
              {items.length === 0
                ? titleQuery
                  ? `No out-of-stock products match “${titleQuery}”.`
                  : "No out-of-stock products found."
                : "All matching out-of-stock products loaded."}
            </Typography>
          }
        >
          <Grid container spacing={2} sx={{ pb: 4 }}>
            {items.map((product) => {
              const imageSrc = product?.featuredImage?.url;
              const variants = product?.variants?.nodes || [];

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {imageSrc ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={imageSrc}
                        alt={
                          product?.featuredImage?.altText ||
                          product?.title ||
                          "Product"
                        }
                        onClick={() => openLightbox(imageSrc, product?.title)}
                        sx={{
                          cursor: "pointer",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.100",
                        }}
                      >
                        <Typography color="text.secondary">No image</Typography>
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {product?.title || "Untitled product"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inventory: {product?.totalInventory ?? 0}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                        <Chip
                          size="small"
                          color="error"
                          label="Out of stock"
                        />
                        <Chip
                          size="small"
                          color="success"
                          label={product?.status || "ACTIVE"}
                        />
                      </Stack>
                      {variants.length > 0 ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 0.5 }}
                          >
                            Variants
                          </Typography>
                          <Stack spacing={0.5}>
                            {variants.map((variant) => (
                              <Typography
                                key={variant.id}
                                variant="caption"
                                color="text.secondary"
                              >
                                {variant.title || "Variant"}:{" "}
                                {variant.inventoryQuantity ?? 0}
                                {variant.sku ? ` · SKU ${variant.sku}` : ""}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      ) : null}
                    </CardContent>
                    <CardActions
                      sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                        disabled={
                          !SHOPIFY_PRODUCT_STORE_URL ||
                          !extractIdFromGid(product?.id)
                        }
                        onClick={() => openShopifyProductInventory(product?.id)}
                      >
                        Update inventory
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </InfiniteScroll>
      )}

      <Dialog
        open={lightbox.open}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <Box sx={{ position: "relative", textAlign: "center" }}>
          <IconButton
            onClick={closeLightbox}
            aria-label="Close image"
            sx={{
              position: "absolute",
              top: -40,
              right: 0,
              color: "common.white",
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {lightbox.title ? (
            <Typography
              variant="subtitle1"
              sx={{ color: "common.white", mb: 1 }}
            >
              {lightbox.title}
            </Typography>
          ) : null}
          <Box
            component="img"
            src={lightbox.src}
            alt={lightbox.title || "Product"}
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        </Box>
      </Dialog>
    </React.Fragment>
  );
}
